import {
  alpha,
  createTheme,
  darken,
  getContrastRatio,
  responsiveFontSizes,
} from "@mui/material";
import { font } from "./font";

// Get value from CSS variable
const getCSSVariableValue = (varName) => {
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
};

// Simple HSL to Hex conversion
const hslToHex = (hslString) => {
  if (!hslString) return null;

  try {
    const [h, s, l] = hslString.split(" ").map((str) => parseFloat(str));
    const hDecimal = h / 360;
    const sDecimal = s / 100;
    const lDecimal = l / 100;

    if (s === 0) {
      const rgb = Math.round(lDecimal * 255);
      return `#${rgb.toString(16).padStart(2, "0")}${rgb
        .toString(16)
        .padStart(2, "0")}${rgb.toString(16).padStart(2, "0")}`;
    }

    const hueToRgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q =
      lDecimal < 0.5
        ? lDecimal * (1 + sDecimal)
        : lDecimal + sDecimal - lDecimal * sDecimal;
    const p = 2 * lDecimal - q;

    const r = Math.round(hueToRgb(p, q, hDecimal + 1 / 3) * 255);
    const g = Math.round(hueToRgb(p, q, hDecimal) * 255);
    const b = Math.round(hueToRgb(p, q, hDecimal - 1 / 3) * 255);

    return `#${r.toString(16).padStart(2, "0")}${g
      .toString(16)
      .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  } catch (error) {
    console.error("Error converting HSL to hex:", error);
    return null;
  }
};

// Get color from CSS variable with fallback
const getColorFromCSS = (varName, fallback) => {
  const hsl = getCSSVariableValue(varName);
  const hex = hslToHex(hsl);
  return hex || fallback;
};

// Define COLORS object with all required colors
export const COLORS = {
  // Original colors (maintain backward compatibility)
  col_1: "#bbdbe3",
  col_2: "#e8eff3",
  col_3: "#272525",
  col_4: "#5B478E",
  primary: "#000000",
  secondary: "#1A73E8",
  text: "#000000",
  icon: "rgba(0, 0, 0, 0.54)",
  background: "#FFFFFF",
  lightBackground: "#F5F8FD",
  header: "#e0e0e0",
  highlight: "#F5F8FD",
  ai: "#8b5cf6",
  accent: "#FFD700",
  success: "#2ECC71",
  error: "#E74C3C",
  warning: "#F1C40F",
  info: "#3498DB",

  // CSS variable based colors (will update dynamically)
  get cssPrimary() {
    return getColorFromCSS("--primary", this.primary);
  },
  get cssSecondary() {
    return getColorFromCSS("--secondary", this.secondary);
  },
  get cssBackground() {
    return getColorFromCSS("--background", this.background);
  },
  get cssForeground() {
    return getColorFromCSS("--foreground", this.text);
  },
  get cssCard() {
    return getColorFromCSS("--card", this.background);
  },
  get cssBorder() {
    return getColorFromCSS("--border", "#e5e7eb");
  },
  get cssMutedForeground() {
    return getColorFromCSS("--muted-foreground", "#6b7280");
  },
  get cssDestructive() {
    return getColorFromCSS("--destructive", this.error);
  },
  get cssCyan() {
    return getColorFromCSS("--cyan", "#06b6d4");
  },
  get cssEmerald() {
    return getColorFromCSS("--emerald", "#10b981");
  },
  get cssPurple() {
    return getColorFromCSS("--purple", "#8b5cf6");
  },
};

// Dynamic theme creation based on current mode
const createDynamicTheme = () => {
  const isDark = document.documentElement.classList.contains("dark");

  // Use CSS variables with fallbacks
  const themeColors = {
    primary: getColorFromCSS("--primary", isDark ? "#06b6d4" : "#000000"),
    secondary: getColorFromCSS("--secondary", isDark ? "#374151" : "#1A73E8"),
    background: getColorFromCSS("--background", isDark ? "#111827" : "#FFFFFF"),
    foreground: getColorFromCSS("--foreground", isDark ? "#f9fafb" : "#000000"),
    muted: getColorFromCSS("--muted", isDark ? "#1f2937" : "#f3f4f6"),
    mutedForeground: getColorFromCSS(
      "--muted-foreground",
      isDark ? "#9ca3af" : "#6b7280"
    ),
    border: getColorFromCSS("--border", isDark ? "#374151" : "#e5e7eb"),
    input: getColorFromCSS("--input", isDark ? "#1f2937" : "#FFFFFF"),
    card: getColorFromCSS("--card", isDark ? "#1f2937" : "#FFFFFF"),
    cardForeground: getColorFromCSS(
      "--card-foreground",
      isDark ? "#f9fafb" : "#000000"
    ),
    destructive: getColorFromCSS("--destructive", "#ef4444"),
    destructiveForeground: getColorFromCSS(
      "--destructive-foreground",
      "#ffffff"
    ),
    cyan: getColorFromCSS("--cyan", "#06b6d4"),
    emerald: getColorFromCSS("--emerald", "#10b981"),
    purple: getColorFromCSS("--purple", "#8b5cf6"),
    highlight: isDark ? "#1f2937" : "#f3f4f6",
    ai: "#8b5cf6",
  };

  const BORDER_RADIUS = "0.6rem";
  const SPACING = 4;

  let theme = createTheme({
    typography: {
      fontFamily: '"Inter", "system-ui", "sans-serif"',
      fontSize: font.size.fs_5, // base font size
      h1: { fontWeight: 800, fontSize: font.size.fs_1 },
      h2: { fontWeight: 700, fontSize: font.size.fs_2 },
      h3: { fontWeight: 600, fontSize: font.size.fs_3 },
      h4: { fontWeight: 600, fontSize: font.size.fs_4 },
      h5: { fontWeight: 600, fontSize: font.size.fs_5 },
      h6: { fontWeight: 600, fontSize: font.size.fs_6 },
      body1: { fontSize: font.size.fs_5 },
      body2: { fontSize: font.size.fs_6 },
      button: {
        fontWeight: 600,
        textTransform: "none",
        fontSize: font.size.fs_5,
      },
      caption: { fontSize: font.size.fs_7 },
      overline: { fontSize: font.size.fs_7 },
    },

    palette: {
      mode: isDark ? "dark" : "light",
      primary: {
        main: themeColors.primary,
        light: alpha(themeColors.primary, 0.1),
        dark: darken(themeColors.primary, 0.2),
        contrastText:
          getContrastRatio(themeColors.primary, themeColors.background) > 4.5
            ? themeColors.background
            : themeColors.foreground,
      },
      secondary: {
        main: themeColors.secondary,
        light: alpha(themeColors.secondary, 0.1),
        dark: darken(themeColors.secondary, 0.2),
        contrastText:
          getContrastRatio(themeColors.secondary, themeColors.background) > 4.5
            ? themeColors.background
            : themeColors.foreground,
      },
      background: {
        default: themeColors.background,
        paper: themeColors.card,
      },
      text: {
        primary: themeColors.foreground,
        secondary: themeColors.mutedForeground,
        disabled: alpha(themeColors.foreground, 0.38),
      },
      divider: alpha(themeColors.border, 0.5),
      error: {
        main: themeColors.destructive,
        light: alpha(themeColors.destructive, 0.1),
        dark: darken(themeColors.destructive, 0.2),
        contrastText: themeColors.destructiveForeground,
      },
      warning: {
        main: "#f59e0b",
        light: alpha("#f59e0b", 0.1),
        dark: darken("#f59e0b", 0.2),
      },
      info: {
        main: themeColors.cyan,
        light: alpha(themeColors.cyan, 0.1),
        dark: darken(themeColors.cyan, 0.2),
      },
      success: {
        main: themeColors.emerald,
        light: alpha(themeColors.emerald, 0.1),
        dark: darken(themeColors.emerald, 0.2),
      },
      action: {
        active: alpha(themeColors.foreground, 0.54),
        hover: alpha(themeColors.foreground, 0.04),
        selected: alpha(themeColors.primary, 0.08),
        disabled: alpha(themeColors.foreground, 0.26),
        disabledBackground: alpha(themeColors.foreground, 0.12),
      },
      // Custom palette extensions for backward compatibility
      highlight: {
        main: themeColors.highlight,
        light: alpha(themeColors.highlight, 0.4),
        dark: darken(themeColors.highlight, 0.2),
      },
      ai: {
        main: themeColors.background, // same as Home icon bg
        light: isDark
          ? darken(themeColors.background, 0.05)
          : alpha(themeColors.background, 0.95), // subtle light hover
        dark: darken(themeColors.background, 0.1), // subtle dark hover
      },
      icon: {
        main: themeColors.mutedForeground,
        light: alpha(themeColors.mutedForeground, 0.1),
        dark: darken(themeColors.mutedForeground, 0.2),
      },
      card: {
        main: themeColors.card,
      },
      header: {
        main: isDark ? "#1f2937" : "#ffffff",
      },
    },
    shape: {
      borderRadius: BORDER_RADIUS,
    },
    spacing: SPACING,
    transitions: {
      duration: {
        shortest: 150,
        shorter: 200,
        short: 250,
        standard: 300,
        complex: 375,
        enteringScreen: 225,
        leavingScreen: 195,
      },
    },
    components: {
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            fontSize: "1.4rem", // SAME visual size as old theme
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            fontWeight: 600,
            textTransform: "none",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-2px)",
            },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${themeColors.cyan}, ${themeColors.emerald})`,
            "&:hover": {
              background: `linear-gradient(135deg, ${darken(
                themeColors.cyan,
                0.1
              )}, ${darken(themeColors.emerald, 0.1)})`,
            },
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              backgroundColor: themeColors.input,
              borderRadius: BORDER_RADIUS,
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: themeColors.primary,
                borderWidth: 2,
              },
            },
          },
        },
        defaultProps: {
          size: "small",
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
          },
          notchedOutline: {
            borderColor: themeColors.border,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            backgroundImage: "none",
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            border: `1px solid ${alpha(themeColors.border, 0.5)}`,
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            h1: "h1",
            h2: "h2",
            h3: "h3",
            h4: "h4",
            h5: "h5",
            h6: "h6",
            body1: "p",
            body2: "p",
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            marginBottom: "4px",
            display: "block",
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            color:
              getContrastRatio(themeColors.primary, themeColors.background) >
              4.5
                ? themeColors.background
                : themeColors.foreground,
          },
        },
      },
     MuiIconButton: {
  defaultProps: {
    size: "small",
  },
  styleOverrides: {
    root: {
      fontSize: "1.4rem",
      borderRadius: BORDER_RADIUS,
    },
  },
},

      MuiIcon: {
        defaultProps: {
          sx: {
            fontSize: font.size.fs_5, // standalone icons
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            textDecoration: "none",
            "&:hover": {
              textDecoration: "underline",
            },
          },
        },
      },
MuiCheckbox: {
  defaultProps: {
    size: "medium",
    color: "primary",
  },
  styleOverrides: {
    root: {
      padding: SPACING * 0.75, // Use SPACING constant instead of theme.spacing()
      "&.Mui-checked": {
        color: themeColors.primary,
        "&.Mui-disabled": {
          color: alpha(themeColors.primary, 0.38),
        },
      },
      "&.Mui-disabled": {
        color: alpha(themeColors.foreground, 0.26),
      },
      "&:hover": {
        backgroundColor: alpha(themeColors.primary, 0.04),
        "&.Mui-disabled": {
          backgroundColor: "transparent",
        },
      },
    },
    colorPrimary: {
      "&.Mui-checked": {
        color: themeColors.primary,
        "&:hover": {
          backgroundColor: alpha(themeColors.primary, 0.08),
        },
      },
    },
    colorSecondary: {
      "&.Mui-checked": {
        color: themeColors.secondary,
        "&:hover": {
          backgroundColor: alpha(themeColors.secondary, 0.08),
        },
      },
    },
    colorError: {
      "&.Mui-checked": {
        color: themeColors.destructive,
        "&:hover": {
          backgroundColor: alpha(themeColors.destructive, 0.08),
        },
      },
    },
    colorWarning: {
      "&.Mui-checked": {
        color: "#f59e0b",
        "&:hover": {
          backgroundColor: alpha("#f59e0b", 0.08),
        },
      },
    },
    colorInfo: {
      "&.Mui-checked": {
        color: themeColors.cyan,
        "&:hover": {
          backgroundColor: alpha(themeColors.cyan, 0.08),
        },
      },
    },
    colorSuccess: {
      "&.Mui-checked": {
        color: themeColors.emerald,
        "&:hover": {
          backgroundColor: alpha(themeColors.emerald, 0.08),
        },
      },
    },
  },
},
      

      MuiSelect: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
          },
        },
        defaultProps: {
          size: "small",
        },
      },
      MuiChip: {
        defaultProps: {
          size: "small",
        },
      },
    },
  });

  return responsiveFontSizes(theme);
};

// Export the main theme (for backward compatibility)
export const themePalette = createDynamicTheme();

// Export function to get current theme
export const getCurrentTheme = () => createDynamicTheme();

// Export function to get theme colors
export const getThemeColors = () => {
  const isDark = document.documentElement.classList.contains("dark");
  return {
    primary: getColorFromCSS("--primary", isDark ? "#06b6d4" : "#000000"),
    secondary: getColorFromCSS("--secondary", isDark ? "#374151" : "#1A73E8"),
    background: getColorFromCSS("--background", isDark ? "#111827" : "#FFFFFF"),
    foreground: getColorFromCSS("--foreground", isDark ? "#f9fafb" : "#000000"),
    mutedForeground: getColorFromCSS(
      "--muted-foreground",
      isDark ? "#9ca3af" : "#6b7280"
    ),
    border: getColorFromCSS("--border", isDark ? "#374151" : "#e5e7eb"),
    card: getColorFromCSS("--card", isDark ? "#1f2937" : "#FFFFFF"),
    cyan: getColorFromCSS("--cyan", "#06b6d4"),
    emerald: getColorFromCSS("--emerald", "#10b981"),
    purple: getColorFromCSS("--purple", "#8b5cf6"),
    highlight: isDark ? "#1f2937" : "#f3f4f6",
    ai: "#8b5cf6",
  };
};

// Export light and dark themes for direct use
export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: { main: "#000000" },
    secondary: { main: "#1A73E8" },
    background: { default: "#FFFFFF", paper: "#FFFFFF" },
    text: { primary: "#000000", secondary: "rgba(0, 0, 0, 0.6)" },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#06b6d4" },
    secondary: { main: "#374151" },
    background: { default: "#111827", paper: "#1f2937" },
    text: { primary: "#f9fafb", secondary: "#9ca3af" },
  },
});

// Default export for backward compatibility
export default createDynamicTheme();
